import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';
import './BarChart.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const BarChart = ({ 
  data = [],           // Array of numeric values OR array of dataset objects
  labels = [],         // Array of string labels
  title = '',          // Chart title
  xAxisTitle = '',     // X-axis title
  yAxisTitle = '',     // Y-axis title
  backgroundColor = '#36A2EB',  // Bar color
  borderColor = '#36A2EB',      // Bar border color
  borderWidth = 1,     // Bar border width
  height = 300,        // Chart height
  showLegend = true,   // Show legend
  showGrid = true,     // Show grid lines
  showTooltips = true, // Show tooltips on hover
  showLabels = false,  // Show data labels on bars
  labelColor = '#000000', // Color for data labels
  responsive = true,   // Responsive chart
  maintainAspectRatio = false, // Maintain aspect ratio
}) => {
  
  // Check if data is in new format (array of dataset objects) or old format (array of numbers)
  let datasets;
  if (data.length > 0 && typeof data[0] === 'object' && data[0].hasOwnProperty('data')) {
    // New format: data is array of dataset objects
    datasets = data;
  } else {
    // Old format: data is array of numbers, convert to dataset format
    datasets = [
      {
        label: yAxisTitle || 'Values',
        data: data,
        backgroundColor: backgroundColor,
        borderColor: borderColor,
        borderWidth: borderWidth,
      },
    ];
  }
  
  // Chart data configuration
  const chartData = {
    labels: labels,
    datasets: datasets,
  };

  // Chart options
  const options = {
    responsive: responsive,
    maintainAspectRatio: maintainAspectRatio,
    plugins: {
      legend: {
        display: showLegend,
        position: 'top',
      },
      title: {
        display: !!title,
        text: title,
        font: {
          size: 16,
          weight: 'bold'
        }
      },
      tooltip: {
        enabled: showTooltips,
        callbacks: {
          label: function(context) {
            const value = context.parsed.y || context.parsed.x;
            return `${context.dataset.label}: ${value.toLocaleString()}`;
          }
        }
      },
      datalabels: {
        display: showLabels,
        anchor: 'end',
        align: 'top',
        color: labelColor,
        font: {
          weight: 'bold',
          size: 12
        },
        formatter: function(value) {
          return value.toLocaleString();
        }
      }
    },
    scales: {
      x: {
        display: true,
        title: {
          display: !!xAxisTitle,
          text: xAxisTitle,
          font: {
            size: 12,
            weight: 'bold'
          }
        },
        grid: {
          display: showGrid,
        },
      },
      y: {
        display: true,
        title: {
          display: !!yAxisTitle,
          text: yAxisTitle,
          font: {
            size: 12,
            weight: 'bold'
          }
        },
        grid: {
          display: showGrid,
        },
        beginAtZero: true,
      },
    },
  };

  // Don't render if no data
  if (!data || data.length === 0 || !labels || labels.length === 0) {
    return (
      <div className="chart-placeholder" style={{ height: `${height}px`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
        <div className="placeholder-icon" style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
        <p style={{ margin: 0, color: '#666' }}>No data available for chart</p>
        <small style={{ color: '#999' }}>Configure your chart to see data</small>
      </div>
    );
  }

  return (
    <div className="bar-chart-container" style={{ height: `${height}px`, width: '100%' }}>
      <Bar data={chartData} options={options} />
    </div>
  );
};

export default BarChart;
